import React, { createContext, useState, useContext, useCallback, useRef } from 'react';
import { getChatSessionsByUserEmail, updateChatTagSession } from '../api/ctrag';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';

const ChatHistoryContext = createContext();

export const ChatHistoryProvider = ({ children }) => {
  const { t } = useTranslation();
  const [chatHistory, setChatHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [selectedChatId, setSelectedChatId] = useState(null);
  const isFetchedRef = useRef(false);

  const fetchChatHistory = useCallback(async () => {
    if (isFetchedRef.current) return;
    setLoading(true);
    try {
      const page_size = 20;
      const response = await getChatSessionsByUserEmail(1, page_size);
      const sortedHistory = response.sort((a, b) => 
        new Date(b.last_chat_timestamp) - new Date(a.last_chat_timestamp)
      );
      setChatHistory(sortedHistory);
      setPage(2);
      if (response.length < page_size) {
        setHasMore(false);
      }
      isFetchedRef.current = true;
    } catch (error) {
      console.error(t('chat.history.fetchFailed') + ':', error);
      message.error(t('chat.history.fetchFailed'));
      isFetchedRef.current = false;
    } finally {
      setLoading(false);
    }
  }, [t]);

  const loadMore = useCallback(async () => {
    if (!hasMore || loading) return;
    setLoading(true);
    try {
      const page_size = 20;
      const response = await getChatSessionsByUserEmail(page, page_size);
      if (response.length < page_size) {
        setHasMore(false);
      }
      setChatHistory(prev => {
        const combined = [...prev, ...response];
        return combined.sort((a, b) => 
          new Date(b.last_chat_timestamp) - new Date(a.last_chat_timestamp)
        );
      });
      setPage(prev => prev + 1);
    } catch (error) {
      console.error(t('chat.history.loadMoreFailed') + ':', error);
      message.error(t('chat.history.loadMoreFailed'));
    } finally {
      setLoading(false);
    }
  }, [hasMore, loading, page, t]);

  const refreshChatHistory = useCallback(() => {
    isFetchedRef.current = false;
    setPage(1);
    setHasMore(true);
    fetchChatHistory();
  }, [fetchChatHistory]);

  const addNewChat = useCallback((newChat) => {
    setChatHistory(prev => {
      const newHistory = [newChat, ...prev];
      return newHistory.sort((a, b) => 
        new Date(b.last_chat_timestamp) - new Date(a.last_chat_timestamp)
      );
    });
    setSelectedChatId(newChat.chat_session_id);
    return newChat;
  }, []);

  const clearSelectedChatId = useCallback(() => {
    setSelectedChatId(null);
  }, []);

  const deleteChat = useCallback(async (chatId) => {
    try {
      await updateChatTagSession(chatId, 'delete');
      
      setChatHistory(prev => prev.filter(chat => chat.chat_session_id !== chatId));
      
      if (selectedChatId === chatId) {
        clearSelectedChatId();
      }
      
      message.success(t('chat.history.deleteSuccess'));
      return true;
    } catch (error) {
      console.error(t('chat.history.deleteFailed') + ':', error);
      message.error(t('chat.history.deleteFailed'));
      return false;
    }
  }, [selectedChatId, clearSelectedChatId, t]);

  return (
    <ChatHistoryContext.Provider value={{
      chatHistory,
      loading,
      hasMore,
      selectedChatId,
      setSelectedChatId,
      clearSelectedChatId,
      fetchChatHistory,
      loadMore,
      refreshChatHistory,
      addNewChat,
      deleteChat
    }}>
      {children}
    </ChatHistoryContext.Provider>
  );
};

export const useChatHistory = () => {
  const context = useContext(ChatHistoryContext);
  if (!context) {
    throw new Error('useChatHistory must be used within a ChatHistoryProvider');
  }
  return context;
}; 